import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Markdown } from '@copilotkit/react-ui';
import {
  Flex,
  Icon,
  Text,
  spaces,
  ColorsV2,
  getToken,
  sizes,
} from '@electricjs/arc';
import styled from 'styled-components';
import { HIDDEN_TOOL_NAMES } from './constants';
import type { CopilotKitMessage } from './types';
import { getMessageContent } from './types';
import { MessageFeedbackActions } from './MessageFeedbackActions';
import { WELCOME_MESSAGE_ID } from '@/types/aiChat';

const MessageGroup = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: ${spaces.space_2xs_rem};
  margin-bottom: ${spaces.space_m_rem};
`;

const MessageBubble = styled.div`
  width: fit-content;
  max-width: 100rem;
  padding: ${spaces.space_xs_rem} ${spaces.space_m_rem};
  background-color: ${getToken(ColorsV2.BACKGROUND_ALT)};
  border-radius: ${sizes.size_8_rem} ${sizes.size_8_rem} ${sizes.size_8_rem} 0;
  word-break: break-word;
  overflow-wrap: break-word;

  /* Apply Inter font family to match Arc design system */
  font-family:
    'Inter, sans-serif',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
  font-weight: 400;
  line-height: 1.5;

  /* Override markdown content font size and color to match theme */
  .copilotKitMarkdownElement,
  .copilotKitMarkdownElement * {
    font-size: ${sizes.size_16_rem};
    color: ${getToken(ColorsV2.TEXT)};
  }

  /* Table cells aren't given the copilotKitMarkdownElement class by CopilotKit,
     so they need the theme color applied separately to stay readable in dark mode */
  .copilotKitMarkdown table,
  .copilotKitMarkdown table * {
    color: ${getToken(ColorsV2.TEXT)};
  }
`;

const AIMessageHeader = styled(Flex)`
  align-items: center;
  gap: ${spaces.space_xs_rem};
  margin-bottom: ${spaces.space_xs_rem};
`;

const ToolCallContainer = styled(Flex)`
  gap: ${spaces.space_xs_rem};
  flex-wrap: wrap;

  /* Only add margin if there are visible children */
  &:not(:empty) {
    margin-top: ${spaces.space_m_rem};
  }
`;

type CustomAssistantMessageProps = {
  message: CopilotKitMessage;
  isStreaming?: boolean;
};

/**
 * Returns true if the assistant message has *some* potentially-visible payload —
 * any text content, or a tool call that isn't on the hidden list.
 *
 * "Potentially visible" because a tool renderer can still decide to render null
 * at runtime (e.g. OPEN_SUPPORT_TICKET's data-fetch phases). That case is handled
 * separately by a post-render emptiness check in the component below.
 */
const hasPotentialContent = (
  message: CopilotKitMessage,
  subComponent: React.ReactElement | null | undefined
): boolean => {
  if (getMessageContent(message).trim() !== '') {
    return true;
  }

  if (subComponent) {
    const toolCall = subComponent.props?.toolCall as
      | { function?: { name?: string } }
      | undefined;
    const name = toolCall?.function?.name;
    if (
      name &&
      HIDDEN_TOOL_NAMES.includes(name as (typeof HIDDEN_TOOL_NAMES)[number])
    ) {
      // Skip and fall through to message.toolCalls check below
    } else {
      return true;
    }
  }

  if (message.toolCalls) {
    const { toolCalls } = message;
    if (toolCalls?.length) {
      return toolCalls.some(
        tool =>
          !HIDDEN_TOOL_NAMES.includes(
            (tool.function?.name ?? '') as (typeof HIDDEN_TOOL_NAMES)[number]
          )
      );
    }
  }

  return false;
};

/**
 * Custom AssistantMessage component that uses CopilotKit's Message type
 */
export const CustomAssistantMessage = ({
  message,
  isStreaming,
}: CustomAssistantMessageProps) => {
  const { t } = useTranslation('ai-chat');
  const { gigawattMessageFeedback } = useFlags();

  // Extract content using type-safe helper function
  const content = getMessageContent(message);

  // Guard against race condition where agent becomes unavailable during Strict Mode re-renders
  // generativeUI is added by CopilotKit at runtime for messages with tool calls
  // NOTE: When messages are restored from Redux, generativeUI is lost but toolCalls data is preserved
  const subComponent = (() => {
    try {
      return message.generativeUI?.();
    } catch (error) {
      // Handle "Agent not found" error during agent initialization flickering
      if (error instanceof Error && error.message.includes('Agent not found')) {
        // Silently return null - the agent will become available shortly
        return null;
      }
      // Re-throw unexpected errors
      throw error;
    }
  })();

  // Post-render emptiness check: a tool renderer can return null (e.g. multi-call
  // tools that fetch data in early phases). When the only body content is an empty
  // tool render, hide the bubble shell rather than showing an empty shell.
  //
  // We toggle `display: none` on the same tree instead of conditionally wrapping —
  // remounting would unmount the tool renderer and re-run its respond() effects.
  const toolContainerRef = useRef<HTMLDivElement>(null);
  const [toolRenderIsEmpty, setToolRenderIsEmpty] = useState(false);
  const hasSubComponent = !!subComponent;

  // Measure whether the tool renderer produced any DOM output. A tool renderer
  // can return null/empty on early passes (e.g. OPEN_SUPPORT_TICKET data-fetch).
  //
  // useEffect (not useLayoutEffect) + MutationObserver avoids scheduling a
  // synchronous setState during the layout commit of an unrelated parent update
  // (e.g. showSuccessToast after invite-admins submit), which React rejects as
  // a nested update (checkForNestedUpdates / "Maximum update depth exceeded").
  //
  // Depends on hasSubComponent (boolean), not subComponent itself — generativeUI()
  // returns a new React element reference every render.
  useEffect(() => {
    if (!hasSubComponent) {
      setToolRenderIsEmpty(false);
      return;
    }

    const node = toolContainerRef.current;
    if (!node) return;

    const measure = () => {
      const next = node.childNodes.length === 0;
      setToolRenderIsEmpty(prev => (prev === next ? prev : next));
    };

    measure();

    const observer = new MutationObserver(measure);
    observer.observe(node, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [hasSubComponent, message.id]);

  if (!hasPotentialContent(message, subComponent)) {
    return null;
  }

  const hasText = content.trim() !== '';
  const hideShell = !hasText && !!subComponent && toolRenderIsEmpty;
  // Hidden tools (e.g. select_toolset) render an empty fragment, which is
  // still a truthy `subComponent` — so we can't gate feedback on
  // `!subComponent` alone, or a text message with only a hidden tool call
  // attached would wrongly lose its feedback control. Reuse the same
  // emptiness measurement as `hideShell` to check for a genuinely visible
  // tool-call chip instead.
  const hasVisibleToolCall = !!subComponent && !toolRenderIsEmpty;

  return (
    <MessageGroup style={hideShell ? { display: 'none' } : undefined}>
      <MessageBubble>
        <AIMessageHeader>
          <Icon icon="sparkles" size="small" />
          <Text variant="body-2">{t('title')}</Text>
          <Text variant="legal">{t('aiAgent')}</Text>
        </AIMessageHeader>

        {content && <Markdown content={content} />}

        {subComponent && (
          <ToolCallContainer ref={toolContainerRef}>
            {subComponent}
          </ToolCallContainer>
        )}
      </MessageBubble>

      {gigawattMessageFeedback &&
        hasText &&
        !hasVisibleToolCall &&
        message.id !== WELCOME_MESSAGE_ID && (
          <MessageFeedbackActions
            messageId={message.id}
            disabled={isStreaming}
          />
        )}
    </MessageGroup>
  );
};
